import { Injectable } from '@angular/core';
import {
  HttpRequest, HttpHandler, HttpEvent,
  HttpInterceptor, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { LoginService } from '../services/login.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(private loginService: LoginService, private router: Router) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Already-retried requests must not trigger another refresh cycle
    const alreadyRetried = request.headers.has('X-Refresh-Retry');

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !alreadyRetried && !this.isRefreshOrLoginRequest(request)) {
          return this.handle401(request, next);
        }
        return throwError(error);
      })
    );
  }

  private isRefreshOrLoginRequest(request: HttpRequest<any>): boolean {
    // Use endsWith to avoid false matches on URLs that contain these words as substrings
    return request.url.endsWith('refreshToken') ||
           request.url.endsWith('/login') ||
           request.url.endsWith('/logout');
  }

  private handle401(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      // Another refresh is already in flight — queue this request until the new token arrives
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.withToken(request, token!)))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.loginService.refreshToken().pipe(
      switchMap(newToken => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(newToken);
        return next.handle(this.withToken(request, newToken));
      }),
      catchError((err: HttpErrorResponse) => {
        this.isRefreshing = false;
        if (err.status === 401) {
          // Genuine auth failure — token is expired beyond the refresh TTL or blacklisted.
          // Clear the session and send the user to login.
          localStorage.removeItem('access_token');
          this.router.navigate(['/login']);
        }
        // For transient errors (5xx, network, etc.) don't wipe the session — the next
        // interaction will trigger another 401 → refresh cycle once the server recovers.
        return throwError(err);
      })
    );
  }

  // Manually set the token on retried requests (JWT module won't re-run for retries)
  // and mark the request so a second 401 doesn't trigger another refresh cycle.
  private withToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        'X-Authorization': `Bearer ${token}`,
        'X-Refresh-Retry': 'true',
      }
    });
  }
}
